import type {
  Content,
  Heading,
  Image,
  List,
  ListItem,
  Paragraph,
  PhrasingContent,
  Root,
  RootContent,
  Text,
} from 'mdast'
import type { ContainerDirective, LeafDirective, TextDirective } from 'mdast-util-directive'
import { toString } from 'mdast-util-to-string'
import type { Plugin } from 'unified'
import { visit } from 'unist-util-visit'
import {
  ANAMNESIS_BLANK_TEXTS,
  ANAMNESIS_EMPTY_ATTRIBUTE,
  ANAMNESIS_TEXTS,
  DEFAULT_ANAMNESIS_EMPTY_TEXT,
  DIRECTIVE_NAMES,
  GALLERY_EMPTY_ATTRIBUTE,
  VARIABLE_REGEX,
  getBlockTitle,
  getDirectiveDefinition,
  getGalleryColumns,
} from './dialect.ts'
import { abbreviateHash, formatInstant, formatIsoDate } from './format.ts'
import type {
  AnamnesisBlankFieldKey,
  AnamnesisBlankForm,
  AnamnesisBlankOption,
  AnamnesisBlankQuestion,
  AnamnesisBlock,
  AnamnesisData,
  LintDiagnostic,
  Variables,
} from './types.ts'
import { normalizeVariablePath, readVariable } from './variables.ts'

export { formatIsoDate }

/**
 * Transforma a árvore Markdown na estrutura do documento Westhetics:
 * - diretivas viram blocos com classe e título (`data.hName` / `hProperties`, que o
 *   remark-rehype honra em qualquer nó); o título é o padrão do dialeto ou o do atributo `title`;
 * - `:::gallery` vira uma grade de `<figure>` com `<img>` e `<figcaption>` (P-PLA-11);
 * - `::anamnesis` vira as seções de pergunta e resposta da anamnese que a API passa em
 *   `anamnesis` (P-DOC-03), a ficha em branco para preencher à mão (`mode: 'blank'`, RN-ANA-24),
 *   ou o texto de `empty` quando não há anamnese respondida;
 * - `{{variable}}` vira `<span class="wx-var">` com o valor ou o placeholder;
 * - `##`, `###` e `####` ganham numeração (1., 1.1, 1.1.1) e id de âncora;
 * - o que não é do dialeto vira aviso, nunca erro silencioso.
 *
 * Roda depois de remark-parse, remark-frontmatter, remark-gfm e remark-directive.
 */

export interface RemarkLegalOptions {
  variables?: Variables
  numbering?: boolean
  /** Coletor de avisos — o parser passa o array e lê depois. */
  diagnostics?: LintDiagnostic[]
  /**
   * As respostas que `::anamnesis` imprime, ou a ficha em branco (`mode: 'blank'`); sem nenhum dos
   * dois, o bloco imprime `empty` e avisa.
   */
  anamnesis?: AnamnesisData | null
}

type Directive = ContainerDirective | LeafDirective | TextDirective

function el(
  tag: string,
  className: string[],
  children: PhrasingContent[] = [],
  extra: Record<string, unknown> = {},
): Paragraph {
  return {
    type: 'paragraph',
    data: { hName: tag, hProperties: { className, ...extra } },
    children,
  }
}

function text(value: string): Text {
  return { type: 'text', value }
}

function slug(s: string) {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export const remarkLegal: Plugin<[RemarkLegalOptions?], Root> = (options = {}) => {
  const { variables, numbering = true, anamnesis = null } = options
  const diagnostics = options.diagnostics ?? []

  return (tree) => {
    // 0. Hora ou proporção no texto ("14:20", "2:1"): o remark-directive lê `:20` como diretiva
    //    de texto, mas nenhuma diretiva do dialeto começa com dígito. Volta a ser texto, sem aviso,
    //    antes de os blocos serem montados (a galeria copia as legendas para dentro das figuras).
    visit(tree, 'textDirective', (node: TextDirective, index, parent) => {
      if (!parent || typeof index !== 'number' || !/^\d+$/.test(node.name)) return
      ;(parent.children as Content[])[index] = text(`:${node.name}${toString(node)}`)
    })

    // 1. Diretivas → blocos do documento
    visit(tree, (node, index, parent) => {
      if (
        node.type !== 'containerDirective' &&
        node.type !== 'leafDirective' &&
        node.type !== 'textDirective'
      )
        return
      const d = node as Directive
      const definition = getDirectiveDefinition(d.name)
      const line = d.position?.start.line

      if (!definition || !DIRECTIVE_NAMES.has(d.name) || definition.type !== d.type) {
        diagnostics.push({
          severity: 'warning',
          code: 'unknown_directive',
          message: `O bloco “${d.name}” não existe no dialeto Westhetics. Veja a lista em docs/styleguide/08-legal-design.md.`,
          line,
        })
        // Renderiza o conteúdo mesmo assim, sem estilo de bloco.
        d.data = {
          hName: d.type === 'textDirective' ? 'span' : 'div',
          hProperties: { className: ['wx-unknown'] },
        }
        return
      }

      if (d.type === 'textDirective') {
        if (d.name === 'u') {
          // :u[texto] — sublinhado (T40). Mesma tag na tela, no HTML e no PDF.
          d.data = { hName: 'u', hProperties: { className: ['wx-u'] } }
          return
        }
        // :term[texto]{def="…"}
        const def = String(d.attributes?.def ?? '')
        d.data = {
          hName: 'dfn',
          hProperties: { className: ['wx-term'], title: def, 'data-def': def },
        }
        return
      }

      if (d.type === 'leafDirective') {
        if (d.name === 'anamnesis') {
          const leaf = d as LeafDirective
          if (!anamnesis) {
            diagnostics.push({
              severity: 'warning',
              code: 'missing_anamnesis',
              message:
                'O bloco ::anamnesis não recebeu as respostas: imprime o texto de empty. Na geração, a API monta o bloco com a anamnese do atendimento.',
              line,
              block: d.name,
            })
          }
          const replacement = anamnesisBlock(leaf, anamnesis)
          if (parent && typeof index === 'number') {
            ;(parent.children as Content[])[index] = replacement
          }
          return
        }
        // ::page-break
        d.data = {
          hName: 'div',
          hProperties: { className: ['wx-page-break'], 'aria-hidden': 'true' },
        }
        return
      }

      const block = d as ContainerDirective
      const isGallery = d.name === 'gallery'
      const emptyText = isGallery
        ? String(block.attributes?.[GALLERY_EMPTY_ATTRIBUTE] ?? '').trim()
        : ''
      // Galeria sem foto e com `empty` não é bloco vazio: é uma etapa sem registro, e diz isso.
      if (block.children.length === 0 && !emptyText) {
        diagnostics.push({
          severity: 'warning',
          code: 'empty_directive',
          message: `O bloco “${d.name}” está vazio.`,
          line,
          block: d.name,
        })
      }

      const className = ['wx-block', `wx-block--${d.name}`]
      const children: RootContent[] = []
      const title = getBlockTitle(d.name, block.attributes)

      if (d.name === 'update') {
        const date = String(block.attributes?.date ?? '')
        const label = date ? `${title} · ${formatIsoDate(date)}` : title
        children.push(el('div', ['wx-block-title'], [text(label ?? '')]))
      } else if (title) {
        children.push(el('div', ['wx-block-title'], [text(title)]))
      }

      if (d.name === 'signatures') {
        // Cada item da lista vira uma linha de assinatura: traço + nome/papel.
        const list = block.children.find((c): c is List => c.type === 'list')
        const items = list?.children ?? []
        for (const item of items) {
          children.push(signatureLine(item))
        }
        const others = block.children.filter((c) => c.type !== 'list')
        children.push(...others)
      } else if (d.name === 'parties') {
        for (const c of block.children) {
          if (c.type === 'list')
            c.data = { hName: 'ul', hProperties: { className: ['wx-parties'] } }
        }
        children.push(...block.children)
      } else if (isGallery) {
        children.push(...galleryChildren(block, emptyText))
      } else {
        children.push(...block.children)
      }

      const replacement: Paragraph = {
        type: 'paragraph',
        data: { hName: 'section', hProperties: { className, 'data-block': d.name } },
        // O handler de parágrafo aceita qualquer filho quando hName é aplicado via applyData.
        children: children as unknown as PhrasingContent[],
      }
      if (parent && typeof index === 'number') (parent.children as Content[])[index] = replacement
    })

    // 2. Âncoras (antes da numeração, para o id não carregar o número) e numeração de títulos
    visit(tree, 'heading', (h: Heading) => {
      const id = slug(toString(h))
      h.data = {
        ...h.data,
        hProperties: { ...(h.data?.hProperties ?? {}), id, className: ['wx-h', `wx-h${h.depth}`] },
      }
    })
    if (numbering) {
      let section = 0
      let sub = 0
      let item = 0
      visit(tree, 'heading', (h: Heading) => {
        if (h.depth === 2) {
          section += 1
          sub = 0
          item = 0
          prefixNumber(h, `${section}.`)
        } else if (h.depth === 3) {
          if (section === 0) section = 1
          sub += 1
          item = 0
          prefixNumber(h, `${section}.${sub}`)
        } else if (h.depth === 4) {
          // Cláusula dentro de cláusula (T40): 1.1.1. Sem ### antes, assume a primeira.
          if (section === 0) section = 1
          if (sub === 0) sub = 1
          item += 1
          prefixNumber(h, `${section}.${sub}.${item}`)
        }
      })
    }

    // 3. Variáveis em texto → <span class="wx-var">
    visit(tree, 'text', (node: Text, index, parent) => {
      if (!parent || typeof index !== 'number') return
      if (!VARIABLE_REGEX.test(node.value)) {
        VARIABLE_REGEX.lastIndex = 0
        return
      }
      VARIABLE_REGEX.lastIndex = 0
      const parts: PhrasingContent[] = []
      let lastIndex = 0
      for (const m of node.value.matchAll(VARIABLE_REGEX)) {
        const start = m.index ?? 0
        if (start > lastIndex) parts.push(text(node.value.slice(lastIndex, start)))
        const path = normalizeVariablePath(m[1] ?? '')
        const value = readVariable(variables, path)
        parts.push({
          type: 'strong',
          data: {
            hName: 'span',
            hProperties: {
              className: ['wx-var', value === undefined ? 'wx-var--empty' : 'wx-var--filled'],
              'data-var': path,
            },
          },
          children: [text(value ?? path)],
        })
        lastIndex = start + m[0].length
      }
      if (lastIndex < node.value.length) parts.push(text(node.value.slice(lastIndex)))
      ;(parent.children as PhrasingContent[]).splice(index, 1, ...parts)
      return index + parts.length
    })
  }
}

function prefixNumber(h: Heading, number: string) {
  h.children.unshift({
    type: 'strong',
    data: { hName: 'span', hProperties: { className: ['wx-num'], 'aria-hidden': 'true' } },
    children: [text(number)],
  })
  h.data = { ...h.data, hProperties: { ...(h.data?.hProperties ?? {}), 'data-number': number } }
}

function signatureLine(item: ListItem): Paragraph {
  const paragraph = item.children.find((c): c is Paragraph => c.type === 'paragraph')
  const content = paragraph?.children ?? [text(toString(item))]
  return el(
    'div',
    ['wx-signature-line'],
    [
      el('span', ['wx-signature-stroke'], []) as unknown as PhrasingContent,
      el('span', ['wx-signature-name'], content) as unknown as PhrasingContent,
    ],
  )
}

/**
 * A galeria de fotos (P-PLA-11): cada `![Legenda](src)` vira `<figure>` com a imagem e a legenda;
 * o parágrafo curto logo depois da imagem (data e quem registrou) é a segunda linha da legenda. O
 * que não é foto fica depois da grade. Sem foto, imprime o texto do atributo `empty`.
 */
function galleryChildren(block: ContainerDirective, emptyText: string): RootContent[] {
  const columns = getGalleryColumns(block.attributes)
  const figures: PhrasingContent[] = []
  const rest: RootContent[] = []
  const items = block.children
  for (let i = 0; i < items.length; i += 1) {
    const node = items[i]!
    if (node.type !== 'paragraph') {
      rest.push(node)
      continue
    }
    const images = node.children.filter((c): c is Image => c.type === 'image')
    if (images.length === 0) {
      rest.push(node)
      continue
    }
    // A segunda linha da legenda: o texto que sobra no parágrafo da foto ou o parágrafo seguinte.
    let detail = trimPhrasing(node.children.filter((c) => c.type !== 'image'))
    if (images.length === 1 && detail.length === 0) {
      const next = items[i + 1]
      if (next?.type === 'paragraph' && !next.children.some((c) => c.type === 'image')) {
        detail = trimPhrasing(next.children)
        i += 1
      }
    }
    if (images.length > 1 && detail.length) rest.push({ type: 'paragraph', children: detail })
    for (const image of images) figures.push(figure(image, images.length === 1 ? detail : []))
  }
  const out: RootContent[] = []
  if (figures.length) {
    out.push(
      el('div', ['wx-gallery', `wx-gallery--${columns}`], figures, {
        'data-columns': String(columns),
      }),
    )
  } else if (emptyText) {
    out.push(el('p', ['wx-gallery-empty'], [text(emptyText)]))
  }
  out.push(...rest)
  return out
}

function figure(image: Image, detail: PhrasingContent[]): PhrasingContent {
  image.data = {
    ...image.data,
    hProperties: { ...(image.data?.hProperties ?? {}), className: ['wx-gallery-image'] },
  }
  const caption: PhrasingContent[] = []
  const alt = image.alt?.trim()
  if (alt)
    caption.push(
      el('span', ['wx-gallery-caption-title'], [text(alt)]) as unknown as PhrasingContent,
    )
  if (detail.length)
    caption.push(el('span', ['wx-gallery-caption-detail'], detail) as unknown as PhrasingContent)
  const children: PhrasingContent[] = [image]
  if (caption.length)
    children.push(el('figcaption', ['wx-gallery-caption'], caption) as unknown as PhrasingContent)
  return el('figure', ['wx-gallery-item'], children) as unknown as PhrasingContent
}

/** Tira o espaço e as quebras de linha das pontas de uma legenda. */
function trimPhrasing(nodes: PhrasingContent[]): PhrasingContent[] {
  const out = [...nodes]
  const isBlank = (n: PhrasingContent | undefined) =>
    n !== undefined && ((n.type === 'text' && n.value.trim() === '') || n.type === 'break')
  while (isBlank(out[0])) out.shift()
  while (isBlank(out[out.length - 1])) out.pop()
  const first = out[0]
  if (first?.type === 'text') out[0] = { ...first, value: first.value.replace(/^\s+/, '') }
  const last = out[out.length - 1]
  if (last?.type === 'text')
    out[out.length - 1] = { ...last, value: last.value.replace(/\s+$/, '') }
  return out
}

/**
 * O bloco de anamnese (P-DOC-03): uma seção por bloco do questionário, cada pergunta com a resposta
 * por extenso; sem resposta imprime "Não respondido", pulada imprime "Pulada", o "descreva" vira
 * "Observação: …"; no fim, protocolo, data e hash abreviado. No modo de ficha em branco (RN-ANA-24),
 * o formulário para preencher à mão (`blankForm`). Sem dados, imprime o texto de `empty`.
 */
function anamnesisBlock(leaf: LeafDirective, data: AnamnesisData | null): Paragraph {
  const title = getBlockTitle(leaf.name, leaf.attributes)
  const emptyText =
    String(leaf.attributes?.[ANAMNESIS_EMPTY_ATTRIBUTE] ?? '').trim() ||
    DEFAULT_ANAMNESIS_EMPTY_TEXT
  const children: RootContent[] = []
  const blank = data !== null && isBlankForm(data)
  if (title) children.push(el('div', ['wx-block-title'], [text(title)]))
  if (!data) {
    children.push(el('p', ['wx-anamnesis-empty'], [text(emptyText)]))
  } else if (isBlankForm(data)) {
    children.push(blankForm(data))
  } else {
    const sections: PhrasingContent[] = data.sections.map(
      (section) =>
        el(
          'section',
          ['wx-anamnesis-section'],
          [
            el(
              'div',
              ['wx-anamnesis-section-title'],
              [text(section.title)],
            ) as unknown as PhrasingContent,
            ...section.items.map((item) => anamnesisItem(item) as unknown as PhrasingContent),
          ],
        ) as unknown as PhrasingContent,
    )
    const meta = [
      `${ANAMNESIS_TEXTS.protocol} ${data.protocolNumber}`,
      `${ANAMNESIS_TEXTS.completedAt} ${formatInstant(data.completedAt)}`,
      `${ANAMNESIS_TEXTS.hash} ${abbreviateHash(data.hash)}`,
    ].join(' · ')
    sections.push(el('p', ['wx-anamnesis-meta'], [text(meta)]) as unknown as PhrasingContent)
    children.push(el('div', ['wx-anamnesis'], sections))
  }
  return {
    type: 'paragraph',
    data: {
      hName: 'section',
      hProperties: {
        className: ['wx-block', 'wx-block--anamnesis'],
        'data-block': leaf.name,
        ...(blank ? { 'data-mode': 'blank' } : {}),
      },
    },
    children: children as unknown as PhrasingContent[],
  }
}

function isBlankForm(data: AnamnesisData): data is AnamnesisBlankForm {
  return (data as AnamnesisBlankForm).mode === 'blank'
}

/** Um elemento como filho de outro (o mesmo `el`, já no tipo que o pai aceita). */
function node(
  tag: string,
  className: string[],
  children: PhrasingContent[] = [],
  extra: Record<string, unknown> = {},
): PhrasingContent {
  return el(tag, className, children, extra) as unknown as PhrasingContent
}

/** As linhas para escrever à mão: decorativas, a altura de uma linha de caneta. */
function writingLines(count: number, className: string[] = []): PhrasingContent[] {
  return Array.from({ length: count }, () =>
    node('span', ['wx-anamnesis-line', ...className], [], { 'aria-hidden': 'true' }),
  )
}

/** "___/___/_____": dia, mês e ano em traços curtos. */
function dateBlank(): PhrasingContent {
  const part = (extra: string[] = []) =>
    node('span', ['wx-anamnesis-date-part', ...extra], [], { 'aria-hidden': 'true' })
  return node(
    'span',
    ['wx-anamnesis-date'],
    [part(), text('/'), part(), text('/'), part(['wx-anamnesis-date-part--year'])],
  )
}

/** Uma caixa para marcar e o rótulo; os exemplos saem entre parênteses, em secondary. */
function checkbox(label: string, examples?: string | null): PhrasingContent[] {
  const detail = examples?.trim()
  return [
    node('span', ['wx-anamnesis-box'], [], { 'aria-hidden': 'true' }),
    node(
      'span',
      ['wx-anamnesis-option-label'],
      [
        text(label),
        ...(detail ? [node('span', ['wx-anamnesis-option-examples'], [text(` (${detail})`)])] : []),
      ],
    ),
  ]
}

/** Os campos de identificação, nesta ordem; nome e endereço ocupam a linha inteira. */
const BLANK_FIELDS = Object.keys(ANAMNESIS_BLANK_TEXTS.fields) as AnamnesisBlankFieldKey[]
const WIDE_FIELDS: ReadonlySet<AnamnesisBlankFieldKey> = new Set(['name', 'address'])

/** "Seus dados": o valor conhecido sai impresso; o resto é linha (o endereço, duas). */
function identification(values: AnamnesisBlankForm['identification']): PhrasingContent {
  const fields = BLANK_FIELDS.map((key) => {
    const value = values?.[key]?.trim()
    const answer = value
      ? [node('div', ['wx-anamnesis-field-value'], [text(value)])]
      : key === 'birthDate'
        ? [dateBlank()]
        : writingLines(key === 'address' ? 2 : 1)
    return node(
      'div',
      ['wx-anamnesis-field'],
      [
        node('div', ['wx-anamnesis-field-label'], [text(ANAMNESIS_BLANK_TEXTS.fields[key])]),
        ...answer,
      ],
      { 'data-field': key, ...(WIDE_FIELDS.has(key) ? { 'data-span': 'wide' } : {}) },
    )
  })
  return node(
    'section',
    ['wx-anamnesis-section', 'wx-anamnesis-identification'],
    [
      node('div', ['wx-anamnesis-section-title'], [text(ANAMNESIS_BLANK_TEXTS.identification)]),
      node('div', ['wx-anamnesis-fields'], fields),
    ],
  )
}

/**
 * Como as opções se arrumam: sim ou não e escala numa linha só; escolha com mais de 6 opções em
 * duas colunas; com rótulo longo, uma por linha; o resto, na mesma linha.
 */
function optionsLayout(
  type: AnamnesisBlankQuestion['type'],
  options: AnamnesisBlankOption[],
): 'inline' | 'list' | 'columns' {
  if (type === 'yes_no' || type === 'scale') return 'inline'
  if (options.length > 6) return 'columns'
  return options.some((o) => o.label.length + (o.examples?.length ?? 0) > 32) ? 'list' : 'inline'
}

/** "Marque uma." ou "Marque todas que se aplicam.", a menos que a explicação já diga como marcar. */
function choiceHint(item: AnamnesisBlankQuestion): string | null {
  if (/\bmarque\b/i.test(item.context ?? '')) return null
  if (item.type === 'multiple_choice') return ANAMNESIS_BLANK_TEXTS.multipleChoice
  if (item.type === 'single_choice' || item.type === 'scale')
    return ANAMNESIS_BLANK_TEXTS.singleChoice
  return null
}

/** "Responda se marcou Sim na anterior." · "Responda se marcou Sim ou Não sei dizer na pergunta 4." */
function conditionText(
  condition: NonNullable<AnamnesisBlankQuestion['condition']>,
  number: number,
  numbers: ReadonlyMap<string, number>,
): string | null {
  const answers = condition.answers.filter((a) => a.trim())
  if (answers.length === 0) return null
  const at = numbers.get(condition.questionKey)
  const where =
    at === undefined
      ? ''
      : at === number - 1
        ? ` ${ANAMNESIS_BLANK_TEXTS.conditionPrevious}`
        : ` ${ANAMNESIS_BLANK_TEXTS.conditionQuestion} ${at}`
  return `${ANAMNESIS_BLANK_TEXTS.conditionPrefix} ${answers.join(` ${ANAMNESIS_BLANK_TEXTS.or} `)}${where}.`
}

/** O "descreva": "Se marcou Sim: A quê?" e duas linhas; sem respostas, vale sempre. */
function followUpLines(followUp: NonNullable<AnamnesisBlankQuestion['followUp']>): PhrasingContent {
  const answers = (followUp.answers ?? []).filter((a) => a.trim())
  const when = answers.length
    ? `${ANAMNESIS_BLANK_TEXTS.followUpPrefix} ${answers.join(` ${ANAMNESIS_BLANK_TEXTS.or} `)}: `
    : ''
  const optional = followUp.isRequired === false ? ` · ${ANAMNESIS_BLANK_TEXTS.optional}` : ''
  return node(
    'div',
    ['wx-anamnesis-followup'],
    [
      node('div', ['wx-anamnesis-followup-label'], [text(`${when}${followUp.label}${optional}`)]),
      ...writingLines(2),
    ],
  )
}

const YES_NO_OPTIONS: AnamnesisBlankOption[] = [
  { label: ANAMNESIS_BLANK_TEXTS.yes },
  { label: ANAMNESIS_BLANK_TEXTS.no },
]

/**
 * Uma pergunta da ficha em branco: o número, a pergunta, a explicação, a condição, a instrução de
 * marcar e o espaço da resposta pelo tipo — uma caixa por opção, a data em traços, uma linha curta
 * para número e três linhas para texto. Pulável ganha "Prefiro não responder"; o "descreva" vem
 * depois, com duas linhas. No A4, a pergunta nunca se separa das opções e das linhas.
 */
function blankItem(
  item: AnamnesisBlankQuestion,
  number: number,
  numbers: ReadonlyMap<string, number>,
): PhrasingContent {
  const children: PhrasingContent[] = [
    node(
      'div',
      ['wx-anamnesis-question'],
      [node('span', ['wx-anamnesis-number'], [text(`${number}.`)]), text(item.question)],
    ),
  ]
  const context = item.context?.trim()
  if (context) children.push(node('div', ['wx-anamnesis-context'], [text(context)]))
  const condition = item.condition ? conditionText(item.condition, number, numbers) : null
  if (condition) children.push(node('div', ['wx-anamnesis-condition'], [text(condition)]))
  const options = item.options?.length ? item.options : item.type === 'yes_no' ? YES_NO_OPTIONS : []
  if (options.length) {
    const hint = choiceHint(item)
    if (hint) children.push(node('div', ['wx-anamnesis-hint'], [text(hint)]))
    children.push(
      node(
        'ul',
        ['wx-anamnesis-options'],
        options.map((o) => node('li', ['wx-anamnesis-option'], checkbox(o.label, o.examples))),
        { 'data-layout': optionsLayout(item.type, options) },
      ),
    )
  } else if (item.type === 'date') {
    children.push(dateBlank())
  } else if (item.type === 'number') {
    children.push(...writingLines(1, ['wx-anamnesis-line--short']))
  } else {
    children.push(...writingLines(3))
  }
  if (item.isSkippable) {
    children.push(
      node(
        'div',
        ['wx-anamnesis-option', 'wx-anamnesis-option--skip'],
        checkbox(ANAMNESIS_BLANK_TEXTS.skip),
      ),
    )
  }
  if (item.followUp) children.push(followUpLines(item.followUp))
  return node('div', ['wx-anamnesis-item'], children, {
    'data-type': item.type,
    'data-number': String(number),
  })
}

/**
 * A ficha em branco (RN-ANA-24, P-ANA-10): "Seus dados" com as linhas da identificação e, depois,
 * os blocos do questionário com as perguntas numeradas na ordem da ficha inteira (é pelo número que
 * a condição aponta "na pergunta 4"). Bloco sem pergunta não sai. O título do bloco, a introdução e
 * a primeira pergunta andam juntos (`wx-anamnesis-lead`): no A4, o título nunca fica sozinho no pé.
 */
function blankForm(data: AnamnesisBlankForm): Paragraph {
  const numbers = new Map<string, number>()
  let count = 0
  for (const section of data.sections) {
    for (const item of section.items) {
      count += 1
      if (item.key) numbers.set(item.key, count)
    }
  }
  let number = 0
  const sections = data.sections
    .filter((section) => section.items.length > 0)
    .map((section) => {
      const intro = section.intro?.trim()
      const [first, ...rest] = section.items.map((item) => blankItem(item, (number += 1), numbers))
      return node(
        'section',
        ['wx-anamnesis-section'],
        [
          node(
            'div',
            ['wx-anamnesis-lead'],
            [
              node('div', ['wx-anamnesis-section-title'], [text(section.title)]),
              ...(intro ? [node('p', ['wx-anamnesis-intro'], [text(intro)])] : []),
              first!,
            ],
          ),
          ...rest,
        ],
      )
    })
  return el(
    'div',
    ['wx-anamnesis', 'wx-anamnesis--blank'],
    [identification(data.identification), ...sections],
  )
}

function anamnesisItem(item: AnamnesisBlock['sections'][number]['items'][number]): Paragraph {
  const state = item.isSkipped ? 'skipped' : item.answer === null ? 'unanswered' : 'answered'
  const answer = item.isSkipped
    ? ANAMNESIS_TEXTS.skipped
    : item.answer === null
      ? ANAMNESIS_TEXTS.unanswered
      : item.answer
  const children: PhrasingContent[] = [
    el('div', ['wx-anamnesis-question'], [text(item.question)]) as unknown as PhrasingContent,
    el('div', ['wx-anamnesis-answer'], [text(answer)]) as unknown as PhrasingContent,
  ]
  const note = item.note?.trim()
  if (note) {
    children.push(
      el(
        'div',
        ['wx-anamnesis-note'],
        [text(`${ANAMNESIS_TEXTS.note}: ${note}`)],
      ) as unknown as PhrasingContent,
    )
  }
  return el('div', ['wx-anamnesis-item'], children, { 'data-state': state })
}
